<template>
    <div>
        <GoBackButtonComponent :path="'/login'" />
    </div>
    
    <section class="m-auto px-2 md:px-0 text-secondary w-full md:w-[30rem] md:max-w-[30rem]">
        <LoadingDialog :visible="loading" />
        <div class="rounded shadow p-3 text-center bg-white">
            <LogoComponent :theme="'dark'" :customClass="'text-4xl md:text-6xl'" />
            <p class="text-2xl font-bold py-4">
                {{ translate('create account', 'ucfirst') }}
            </p>
            <p>
                {{ translate('Please provide your information below to proceed with registering your account.') }}
            </p>
            <InputText 
                v-model="authForm.name"
                :style="getStyle('name')"
                class="w-full py-2 px-3 rounded-lg border border-color-iron mt-5 border-focus-purple-suit"
                type="text"
                :placeholder="translate('name', 'ucfirst')"
            />
            <InputText 
                v-model="authForm.email"
                :style="getStyle('email')"
                class="w-full py-2 px-3 rounded-lg border border-color-iron mt-5 border-focus-purple-suit"
                type="text"
                :placeholder="translate('email', 'ucfirst')"
            />
            <Password 
                v-model="authForm.password"
                class="mt-5 w-full"
                :input-style="getStyle('password')"
                :input-class="'w-full py-2 px-3 rounded-lg border border-color-iron border-focus-purple-suit'"
                :placeholder="translate('password', 'ucfirst')"
                :feedback="false"
                toggleMask 
            />
            <div class="mt-5 flex justify-center">
                <Checkbox 
                    class="my-auto mr-2 rounded" 
                    v-model="authForm.accepted" 
                    :binary="true" 
                    :class="getClassForAcceptedTerms()" 
                    :input-class="getClassForAcceptedTerms()"
                />
                <span>
                    {{ translate('I agree and accept the') }}
                    <router-link :to="'/terms-policy'" class="text-carrot underline font-bold">
                        {{ translate('terms of use', 'ucfirst') }}
                    </router-link>.
                </span>
            </div>
            <Button 
                class="mt-5 w-full bg-purple-suit-forced text-white p-1"
                :label="translate('create account', 'ucfirst')"
                @click.prevent="register()"
            />
            <p class="mt-5 fill-text-line text-secondary opacity-50">
                {{ translate('Do you already have an account?') }}
            </p>
            <router-link :to="'/login'">
                <Button 
                    class="no-bg-button mt-5 w-full p-2 rounded"
                    unstyled
                    :label="translate('log in', 'ucfirst')"
                />
            </router-link>
        </div>
    </section>
</template>
<script setup>
    import { ref } from 'vue';
    import InputText from 'primevue/inputtext';
    import Password from 'primevue/password';
    import Checkbox from 'primevue/checkbox';
    import Button from 'primevue/button';
    import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
    import { useToast } from 'primevue/usetoast';
    import { showToast } from '@/composables/toast';
    import { sleep } from '@/composables/sleep';
    import userService from '@/services/UserService';
    import urlHandler from '@/composables/urlHandler';
    import GoBackButtonComponent from '@/components/misc/GoBackButtonComponent.vue';
    import LogoComponent from '@/components/LogoComponent.vue';
    import { translate } from '@/composables/translate';

    const toast = useToast();
    
    let loading = ref(false);
    let authForm = ref({
        name: '',
        email: '',
        password: '',
        accepted: false
    });
    let validation = ref({
        name: 'Please provide your name',
        email: 'Please provide your email address',
        password: 'Please enter your password'
    });

    async function register(){
        let valid = true;
        for(let attr of Object.keys(validation.value)){
            if(authForm.value[attr] == ''){
                showToast(toast, translate(validation.value[attr]), 'error');
                valid = false;
                break;
            }
        }
        if(!valid) {
            return;
        }

        const emailValidation = userService.checkEmail(authForm.value.email);
        if(!emailValidation){
            showToast(toast, translate('the email address is invalid.', 'ucfirst'), 'error');
            return;
        }

        const passValidation = userService.checkPassword(authForm.value.password);
        if(!passValidation.success){
            showToast(toast, passValidation.message, 'error');
            return;
        }

        if(!authForm.value.accepted){
            showToast(toast, translate('You must agree to our terms to create your account.'), 'info');
            return;
        }

        loading.value = true;
        const response = await userService.register(authForm.value);
        loading.value = false;

        if(!response.success()){
            showToast(toast, response.getMessage(), 'error');
            return;
        }

        showToast(toast, translate('Account successfully created. You will be redirected shortly.'));
        await sleep(500);

        urlHandler.redirectAndScroll('/email/verify', { email: authForm.value.email });
    }

    function getStyle(type = 'email'){
        let notFilledStyle = {
            'background-color': '#e3e3e3',
            'color': '#fff'
        }
        if(authForm.value[type] != undefined && authForm.value[type].length == 0)
            return notFilledStyle;
        return {};
    }

    function getClassForAcceptedTerms(){
        return authForm.value.accepted ? 'bg-purple-suit-forced' : '';
    }
</script>